import type { EmailTemplateKey, Language } from '@lms/api-client/backend';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@lms/ui/card';
import { Label } from '@lms/ui/label';
import { type FormEvent, useState } from 'react';
import { useTranslations } from 'use-intl';
import { SimpleSelect } from '@/components/simple-select';
import { SubmitButton } from '@/components/submit-button';
import { addEmailTemplate } from '@/features/email/email-api';
import { supportedLanguages } from '@/features/locale/supported-languages';

const WHAT = 'email_template';
const TEMPLATE_KEYS: EmailTemplateKey[] = ['magiclink', 'invite'];

// Ports app/[brand]/admin/email/_components/add-email-template.tsx. The Next form
// used `useActionState(addEmailTemplate, ...)` with a server action; here the
// selects are controlled state and the async `addEmailTemplate` is called on
// submit. The current site id is passed in, and the parent revalidates its SWR
// list via `onCreated` in place of `revalidatePath`.
export function AddEmailTemplate({
  siteId,
  onCreated,
}: {
  siteId: number;
  onCreated: () => void;
}) {
  const t = useTranslations();
  const [template, setTemplate] = useState('');
  const [language, setLanguage] = useState('');
  const [generalError, setGeneralError] = useState<string | undefined>(
    undefined,
  );
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setGeneralError(undefined);
    setMessage(undefined);

    if (!template || !language) {
      return;
    }

    setPending(true);
    try {
      const result = await addEmailTemplate({
        template: template as EmailTemplateKey,
        language: language as Language,
        siteId,
      });
      if (result.ok) {
        setMessage(
          t('admin.added', { what: t(`admin.${WHAT}`).toLowerCase() }),
        );
        setTemplate('');
        setLanguage('');
        onCreated();
      } else {
        setGeneralError(result.message);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('admin.add', {
            what: t(`admin.${WHAT}`).toLowerCase(),
          })}
        </CardTitle>
        <CardDescription>{t('admin.for_this_site')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="m-auto max-w-md" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-4">
            <div className="space-y-2">
              <Label htmlFor="template">{t('admin.template')}</Label>
              <SimpleSelect
                name="template"
                value={template}
                onValueChange={setTemplate}
                placeholder={t('admin.template')}
                options={TEMPLATE_KEYS.map((key) => ({
                  value: key,
                  label: t(`email.Templates.${key}.name`),
                }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="language">{t('admin.language')}</Label>
              <SimpleSelect
                name="language"
                value={language}
                onValueChange={setLanguage}
                placeholder={t('admin.language')}
                options={supportedLanguages.map((lng) => ({
                  value: lng,
                  label: t(`languages.${lng}`),
                }))}
              />
            </div>

            {generalError && (
              <div className="w-full text-red-500">{generalError}</div>
            )}
            {message && <div className="w-full text-green-500">{message}</div>}
            <SubmitButton
              label={t('admin.add', {
                what: t(`admin.${WHAT}`).toLowerCase(),
              })}
              pending={pending}
              disabled={!template || !language}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
